import {Api, CityUtil, Currency, LogManager, Model, ModelRow, ModelRowType, StringUtil} from "@mcleod/core";
import {
    BlurEvent,
    ChangeEvent,
    DataSourceMode,
    DropdownItem,
    EditRowDecorator,
    Label,
    Layout,
    LookupModelSelectionEvent,
    ScreenStack,
    Snackbar,
    TableRow,
    ValidationResult
} from "@mcleod/components";
import { ModelUsers } from "@mcleod/common";
import { LMEGeneralSettings, LMEUserSettings } from "@mcleod/general";
import { AutogenLayoutPostToLoadBoards } from "./autogen/AutogenLayoutPostToLoadBoards";
import { OrderMultiSelectErrorModal } from "@mcleod/general/src/OrderMultiSelectErrorModal";
import { getDispatchControlValue } from "@mcleod/general/src/models/ModelDispatchControl";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";

const log = LogManager.getLogger("lme.dispatch.PostToLoadBoards");

const equipmentTypeOptionsMap = {
    tarps: "T",
    expedited: "E"
};

interface LoadBoardServices {
    id: string,
    description: string,
    service_type: string,
}

export class PostToLoadBoards extends AutogenLayoutPostToLoadBoards {
    private originalLoadedArriveEarlyValue: string;
    private originalLoadedArriveLateValue: string;
    private pbwLoadboardServiceContainsDatRest: boolean;
    private VALIDATION_MESSAGE = "Load board user is not authorized to post to DAT loadboard";

    private async setDefaultValues() {
        const row = this.mainDataSource.activeRow;
        if (!row) return;

        if (StringUtil.isEmptyString(row.get("pnn_trailer_length"))) {
            if (!StringUtil.isEmptyString(row.get("equipment_type_id"))) {
                await Model.searchSingleRecord("lme/dispatch/match-trailer-type", { id: row.get("equipment_type_id") },
                    "lme/dispatch/EquipmentTypeAppliesToTrailer").then(equipRow => {
                        row.set("pnn_trailer_length", equipRow.get("equip_length"));
                        row.set("pnn_trailer_width", equipRow.get("equip_width"));
                    });
            }

            // If still not set after checking equipment type, use default values
            if (StringUtil.isEmptyString(row.get("pnn_trailer_length"))) {
                row.set("pnn_trailer_length", 53);
                row.set("pnn_trailer_width", 102);
            }
        }
        this.mainDataSource.displayDataInBoundComponents();
    }

    override onLoad() {

        this.textboxVendors.addBeforeLookupModelSearchListener(event => {
            event.filter.is_active = "Y";
        });

        this.textboxOverrideArriveLate.addlValidationCallback = (_value: string, _mode: DataSourceMode): ValidationResult => {
            return this.validateArrivalWindow();
        };

        this.textboxOverrideArriveEarly.addlValidationCallback = (_value: string, _mode: DataSourceMode): ValidationResult => {
            return this.validateArrivalWindow();
        };

        Model.searchSingleRecord("lme/powerbroker/pbw-loadboard-services", { service_type: "R" }).then(
            async (result) => {
                this.panelEquipTypeOptions.visible = StringUtil.equalsIgnoreCase(result?.data?.is_active, "Y");
            }
        );

        this.setDefaultValues();
    }

    async textboxVendorsOnLookupModelSelection() {
        if (ArrayUtil.isEmptyArray(this.textboxVendors.lookupModelData)) {
            return;
        }

        if (this.hasPreferredVendorWithServiceType("T")) {
            const isValidUser = await this.validateLoadBoardUser();
            this.updateLoadBoardUserValidationMessage(isValidUser);
        } else {
            this.hideValidationMessageAndUpdatePanel();
        }
    }


    async postingCityStateOnBlur(event: BlurEvent) {
        if (StringUtil.isEmptyString(this.postingCityState.getBasicValue())) {
            this.populatePostingCity(
                this.mainDataSource.activeRow.get("override_posting_city_id")
            );
            Snackbar.showWarningSnackbar(
                "Posting City / State / Zip can not be empty."
            );
        }
    }

    async textboxOverrideArriveEarlyOnBlur(event: BlurEvent) {
        const buttons = document.getElementsByClassName(
            "cmp-base btn-base btn-round"
        );

        const panel = document.querySelector(".cmp-base.pnl-panel.ovr-popup");
        const buttonArray = Array.from(buttons);
        if (buttonArray.some((button) => button === event.relatedTarget) || panel) {
            return; // Skip if the focus moves to the date button or if the date picker panel is open.
        }

        if (StringUtil.isEmptyString(this.textboxOverrideArriveEarly.text)) {
            this.textboxOverrideArriveEarly.text = this.originalLoadedArriveEarlyValue;
            Snackbar.showWarningSnackbar("Override Pickup Time cannot be empty.");
        }
        // Re-run validation on both fields so arrival window error indicators stay in sync
        this.textboxOverrideArriveEarly.validate(false, false);
        this.textboxOverrideArriveLate.validate(false, false);
    }


    async textboxOverrideArriveLateOnBlur(event: BlurEvent) {
        const buttons = document.getElementsByClassName(
            "cmp-base btn-base btn-round"
        );

        const panel = document.querySelector(".cmp-base.pnl-panel.ovr-popup");
        const buttonArray = Array.from(buttons);
        if (buttonArray.some((button) => button === event.relatedTarget) || panel) {
            return; // Skip if the focus moves to the date button or if the date picker panel is open.
        }

        if (StringUtil.isEmptyString(this.textboxOverrideArriveLate.text)) {
            this.textboxOverrideArriveLate.text = this.originalLoadedArriveLateValue;
            Snackbar.showWarningSnackbar("Override Latest Arrival cannot be empty.");
        }
        // Re-run validation on both fields so arrival window error indicators stay in sync
        this.textboxOverrideArriveEarly.validate(false, false);
        this.textboxOverrideArriveLate.validate(false, false);
    }

    validateArrivalWindow(): ValidationResult | null {
        const row = this.mainDataSource.activeRow;
        if (!row) return null;

        // Only validate when the late field is visible (it's hidden when schedArriveLate is empty)
        if (!this.textboxOverrideArriveLate.visible) return null;

        const earlyValue = row.get("override_arrive_early");
        const lateValue = row.get("override_arrive_late");

        if (!earlyValue || !lateValue) return null;

        const earlyDate = new Date(earlyValue);
        const lateDate = new Date(lateValue);

        if (lateDate <= earlyDate) {
            return {
                isValid: false,
                component: this.textboxOverrideArriveLate,
                caption: "Override Latest Arrival",
                validationMessage: "Override Latest Arrival must be after Override Pickup Time."
            };
        }
        return null;
    }

    async setDefaultById(stopId: string, movementId: string, exceptionsList: any, isChangedMovement: boolean) {
        if (!StringUtil.isEmptyString(stopId)) {
            Model.searchSingleRecord("lme/dispatch/stop", { id: stopId }).then(
                async (result) => {
                    await this.setDefaultsByStop(result, movementId, exceptionsList, isChangedMovement);
                }
            );
        }
    }


    async setDefaultsByStop(stopInfo: ModelRow, movementId: string, exceptionsList: any, isChangedMovement: boolean) {
        this.setDisplayEquipTypeOptions();
        this._setPreferredVendorLookupModelData(this.mainDataSource.activeRow);
        await PostToLoadBoards.setUserInfoFields(
            this.mainDataSource.activeRow,
            this.textboxPnnLoadboardUserId,
            this.textboxPnnLoadboardUserIdOnLookupModelSelection
        );
        this.displayPostPreventionReasons(this.mainDataSource.activeRow.get("id"), this.mainDataSource.activeRow.get("loadboard"), exceptionsList);
        await this._setEquipFields();
        await this._setRateFields(movementId);
        await this._setPostingCityField(stopInfo, isChangedMovement);
        await this._setOverrideStopFields(stopInfo, isChangedMovement);
        this.mainDataSource.displayDataInBoundComponents();
        this.originalLoadedArriveEarlyValue = this.textboxOverrideArriveEarly.text;
        this.originalLoadedArriveLateValue = this.textboxOverrideArriveLate.text;
        this.postingCityState.textCombined.text = CityUtil.formatCityStateZip(stopInfo.get("city_name"), stopInfo.get("state"), stopInfo.get("zip_code"))
    }


    private _setPreferredVendorLookupModelData(row: ModelRow) {
        const lookupData = row?.get("preferred_vendors_lookup_data");
        if (lookupData == null) {
            this.switchPostToVendors.checked = StringUtil.isEmptyString(lookupData);
            if (this.switchPostToVendors.checked) {
                this.textboxVendors.visible = false;
                this.textboxVendors.required = false;
            } else {
                this.textboxVendors.visible = true;
                this.textboxVendors.required = true;
            }
            return;
        } else {
            const newLookupData = lookupData.map(data => {
                const row = new ModelRow("lme/powerbroker/pbw-loadboard-services", false, { ...data });
                row.type = ModelRowType.LOOKUP_MODEL_DATA;
                return row;
            });
            row.setLookupModelData("preferred_vendors", newLookupData);
            row.set("preferred_vendors_lookup_data", undefined);
        }
    }

    async switchPostToVendorsOnChange(event: ChangeEvent) {
        this.textboxVendors.visible = !event.newValue;
        this.textboxVendors.required = !event.newValue;

        if (event.newValue && this.pbwLoadboardServiceContainsDatRest) {
            const isValidUser = await this.validateLoadBoardUser();
            this.updateLoadBoardUserValidationMessage(isValidUser);
        } else {
            this.hideValidationMessageAndUpdatePanel();
        }
    }

    private updateLoadBoardUserValidationMessage(isValid: boolean) {
        const validationMessage = this.VALIDATION_MESSAGE;
        if (isValid) {
            this.hideValidationMessageAndUpdatePanel();
        } else {
            const unauthorizedReasonLabel = this.findComponentById("validationMessageLabel") as Label;
            if (unauthorizedReasonLabel) {
                unauthorizedReasonLabel.visible = true;
            } else {
                const label = new Label({ text: `• ${validationMessage}`, rowBreak: true, id: "validationMessageLabel" });
                this.layoutLbReasonsPanel.lbReasonsPanel.add(label);
            }
            this.setReasonsPanelVisibility(true);
        }
    }

    private setReasonsPanelVisibility(visible: boolean) {
        this.layoutLbReasonsPanel.visible = visible;
        this.panelMainLbReasons.visible = visible;
        this.layoutLbReasonsPanel.lbReasonsPanel.visible = visible;
        this.layoutLbReasonsPanel.labelDetails.visible = visible;
        this.layoutLbReasonsPanel.horizontalspacer1.visible = visible;
    }

    async switchDefaultUserOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.panelUserInfo.visible = !event.newValue;
            this.textboxDefaultUserPostingRole.visible = event.newValue;
            if (event.newValue) {
                this.textboxPnnLoadboardUserId.clear();
                this.textboxPnnCallback.clear();
            } else if (!event.newValue) {
                const lmData = await this.getCurrentUserLookupModelData();
                if (lmData) {
                    this.textboxPnnLoadboardUserId.text = lmData.get("name");
                    this.textboxPnnLoadboardUserId['_lookupModelData'] = [lmData];
                    this.textboxPnnCallback.text = lmData.get("phone");
                }
            }
        }
    }

    switchTargetRateOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.panelRates.visible = !event.newValue;
            if (event.newValue) {
                this.textboxPnnRate.clear();
                this.textboxPnnRateType.clear();
            }
        }
    }

    async displayPostPreventionReasons(order: string, loadboard: string, exceptionsList: Array<string>) {
        if (StringUtil.isEmptyString(order)) {
            return;
        }



        const pbwLoadboardServices = await this.getPbwLoadboardServiceList();

        if (pbwLoadboardServices?.length > 0 && pbwLoadboardServices.includes("T")) {
            this.pbwLoadboardServiceContainsDatRest = true;
            const shouldValidateUser = this.switchPostToVendors.checked || this.hasPreferredVendorWithServiceType("T");
            if (shouldValidateUser) {
                const isValidUser = await this.validateLoadBoardUser();
                if (!isValidUser) {
                    // if exceptionsList == null then create a new array
                    if (exceptionsList == null) {
                        exceptionsList = [];
                    }
                    exceptionsList.push(this.VALIDATION_MESSAGE);
                }
            }
        }

        const reasonsList = exceptionsList;
        if (reasonsList) {
            if (reasonsList.length > 0) {
                this.setReasonsPanelVisibility(true);
                reasonsList.forEach((reason: string) => {
                    if (reason === this.VALIDATION_MESSAGE) {
                        const existingLabel = this.findComponentById("validationMessageLabel");
                        if (!existingLabel) {
                            const label = new Label({ text: `• ${reason}`, rowBreak: true, id: 'validationMessageLabel' });
                            this.layoutLbReasonsPanel.lbReasonsPanel.add(label);
                        }
                    } else {
                        const label = new Label({ text: `• ${reason}`, rowBreak: true });
                        this.layoutLbReasonsPanel.lbReasonsPanel.add(label);
                    }
                });
            } else {
                this.setReasonsPanelVisibility(false);
            }
        }
    }

    private async validateLoadBoardUser(): Promise<boolean> {
        const response = await Api.post("lme/general/powerbroker/authorize", {
            user: this.mainDataSource.activeRow.get("pnn_loadboard_user_id"),
            action_type: "validate_token"
        });
        return response?.data[0]?.does_user_exist ?? false;
    }

    private hasPreferredVendorWithServiceType(serviceType: string): boolean {
        const lmDataFromRow = this.mainDataSource.activeRow.getLookupModelData("preferred_vendors");
        if (!lmDataFromRow?.length) return false;

        const vendorServiceTypes = lmDataFromRow.map((data: ModelRow<any>) =>
            data.get("service_type"));
        return vendorServiceTypes.includes(serviceType);
    }

    async _setOverrideStopFields(stopInfo: ModelRow, isChangedMovement: boolean) {
        const schedArriveLate = stopInfo.get("sched_arrive_late");
        const orderRow = this.mainDataSource.activeRow;

        const overrideArriveEarly = orderRow.get("override_arrive_early");
        const overrideArriveLate = orderRow.get("override_arrive_late");

        if (StringUtil.isEmptyString(overrideArriveEarly) || isChangedMovement) {
            orderRow.set("override_arrive_early", stopInfo.get("sched_arrive_early"));
        } else {
            orderRow.set("override_arrive_early", orderRow.get("override_arrive_early"));
        }

        if (StringUtil.isEmptyString(schedArriveLate) && StringUtil.isEmptyString(overrideArriveLate)) {
            this.textboxOverrideArriveLate.visible = false;
            this.andLabel.visible = false;
        } else if (StringUtil.isEmptyString(overrideArriveLate)) {
            orderRow.set("override_arrive_late", stopInfo.get("sched_arrive_late"));
            this.textboxOverrideArriveLate.visible = true;
            this.andLabel.visible = true;
        } else {
            orderRow.set("override_arrive_late", orderRow.get("override_arrive_late"));
            this.textboxOverrideArriveLate.visible = true;
            this.andLabel.visible = true;
        }

    }

    async _setPostingCityField(stopInfo: ModelRow, isChangedMovement: boolean) {
        const originId = stopInfo.get("location_id");
        let locationCityId = null;
        let pnnOverrideCityId = null;
        let originStopCityId = null;
        if (!isChangedMovement && !StringUtil.isEmptyString(this.mainDataSource.activeRow.get("override_posting_city_id"))
        ) {
            await this.populatePostingCity(
                this.mainDataSource.activeRow.get("override_posting_city_id")
            );
        } else if (!StringUtil.isEmptyString(originId)) {
            pnnOverrideCityId = await this.getOverrideLocationCityId(originId);
            if (!StringUtil.isEmptyString(pnnOverrideCityId)) {
                await this.populatePostingCity(await pnnOverrideCityId);
            } else {
                locationCityId = await this.getLocationCityId(originId);
                await this.populatePostingCity(await locationCityId);
            }
        } else {
            originStopCityId = stopInfo.get("city_id");
            await this.populatePostingCity(await originStopCityId);
        }
    }

    async getOverrideLocationCityId(originLocationId: string): Promise<string> {
        if (originLocationId) {
            const result = await this.sourceLocation.search({ id: originLocationId });
            const rowLocation = result?.modelRows[0];
            return rowLocation?.get("pnn_city_id");
        }
    }

    async getLocationCityId(originLocationId: string): Promise<string> {
        if (originLocationId) {
            const result = await this.sourceLocation.search({ id: originLocationId });
            const rowLocation = result?.modelRows[0];
            return rowLocation?.get("city_id") as string;
        }
    }

    async populatePostingCity(cityId: string) {
        if (cityId) {
            const cityRow = await Model.searchSingleRecord("common/city", { id: cityId });
            if (cityRow) {
                this.displayPostingCityData(cityRow);
            }
        }
    }

    displayPostingCityData(cityRow: any) {
        if (cityRow) {
            const defaultData = new ModelRow(this.sourceCity.layoutName, false, {
                override_posting_city_id: cityRow.get("id"),
                name: cityRow.get("name"),
                state_id: cityRow.get("state_id"),
                zip_code: cityRow.get("zip_code")
            });
            this.mainDataSource.activeRow.set("override_posting_city_id", cityRow.get("id")
            );
            this.postingCityState.displayData(defaultData, null, 0);
            this.postingCityState.tooltipDynamicContent =
                this.postingCityState.makeCityStateTooltipContent(cityRow.get("id"));
        }
    }

    async postDataSources(): Promise<boolean> {
        log.debug("posting to load board");

        try {
            this.mainDataSource.activeRow.set("loadboard", "Y");

            const mainPost = await this.mainDataSource.post();
            log.debug("posted row", mainPost);

            if (this.layoutCarrierRates.mainDataSource.hasChanged()) {
                const rateChangesPost = await this.layoutCarrierRates.mainDataSource.post();
                log.debug("Posted target and max pay changes", rateChangesPost);
            }

            return true; // indicating success
        } catch (error) {
            log.error("Error while posting data sources", error);
            return false; // indicating failure
        }
    }


    public static async setUserInfoFields(modelRow: ModelRow, textboxPnnLoadboardUserId: any, textboxPnnCallback: any) {
        let userId = modelRow.get("pnn_loadboard_user_id");
        if (userId == null)
            userId = modelRow.get("entered_user_id");
        if (userId == null)
            userId = LMEUserSettings.getUserId()

        if (userId != null) {
            await new ModelUsers().searchSingle({ id: userId }, "common/UserLookup").then(userRow => {
                if (!userRow) return;

                const lmData = new ModelRow("common/users", false, {
                    id: userRow.get("id"),
                    name: userRow.get("name"),
                    phone: userRow.get("phone")
                });
                lmData.type = ModelRowType.LOOKUP_MODEL_DATA;
                modelRow.setLookupModelData("pnn_loadboard_user_id", lmData);

                if (!modelRow.get("pnn_loadboard_user_id"))
                    modelRow.set("pnn_loadboard_user_id", userRow.get("id"));

                if (!modelRow.get("pnn_callback"))
                    modelRow.set("pnn_callback", userRow.get("phone"));
            })
        }
        textboxPnnLoadboardUserId?.addLookupModelSelectionListener(textboxPnnCallback);
    }

    private textboxPnnLoadboardUserIdOnLookupModelSelection = (event: LookupModelSelectionEvent) => {
        if (event.hasSelection() === true && this?.mainDataSource?.activeRow) {
            this.mainDataSource.activeRow.set("pnn_callback", event.selection.get("phone"));
            this.textboxPnnCallback.displayData(this.mainDataSource.activeRow, null, 0);
            this.textboxPnnCallback.validate(false, false);
        }
        if (event.hasSelection() === true) {
            if (this.pbwLoadboardServiceContainsDatRest && (this.switchPostToVendors.checked || this.hasPreferredVendorWithServiceType("T"))) {
                if (this.pbwLoadboardServiceContainsDatRest && (this.switchPostToVendors.checked || this.hasPreferredVendorWithServiceType("T"))) {
                    this.validateLoadBoardUser().then(response => {
                        this.updateLoadBoardUserValidationMessage(response);
                    });
                }
            } else {
                this.hideValidationMessageAndUpdatePanel();
            }
        }
    }

    private hideValidationMessageAndUpdatePanel() {
        const unauthorizedReasonLabel = this.findComponentById("validationMessageLabel") as Label;
        if (unauthorizedReasonLabel) {
            unauthorizedReasonLabel.visible = false;
        }
        const labelReasons = Array.from(this.layoutLbReasonsPanel.lbReasonsPanel.components)
            .filter(component => component instanceof Label && component.id !== "validationMessageLabel"
                && component.id !== "labelLoadBoardError" && component.id !== "labelDetails").length;

        if (labelReasons === 0) {
            this.setReasonsPanelVisibility(false);
        }
    }


    private async _setEquipFields() {
        const row = this.mainDataSource.activeRow;
        if (!StringUtil.isEmptyString(row.get("equipment_type_id"))) {
            await Model.searchSingleRecord("lme/dispatch/match-trailer-type", { id: row.get("equipment_type_id") },
                "lme/dispatch/EquipmentTypeAppliesToTrailer").then(equipRow => {
                    row.set("pnn_trailer_length", equipRow.get("equip_length"));
                    row.set("pnn_trailer_width", equipRow.get("equip_width"));
                })
        }

        // If pnn_trailer_length is not set above and is currently null, set it to 53
        if (StringUtil.isEmptyString(row.get("pnn_trailer_length"))) {
            row.set("pnn_trailer_length", 53);
        }

        // If the weight is null, default it to 40,000
        if (StringUtil.isEmptyString(row.get("weight"))) {
            row.set("weight", 40000);
        }
    }

    private async _setRateFields(movementId: string) {
        const orderRow = this.mainDataSource.activeRow;
        const selectedMovementId = StringUtil.isEmptyString(movementId) ? orderRow.get("curr_movement_id", "") : movementId;
        const isPostedMovementRowSelected = StringUtil.equalsIgnoreCase(movementId, orderRow.get("loadboard_post_movement_id"));
        this.layoutCarrierRates.buttonOverrideTargetPay.visible = false;
        this.layoutCarrierRates.buttonOverrideMaxPay.visible = false;
        if (selectedMovementId != null) {
            await this.layoutCarrierRates.mainDataSource.search({ id: selectedMovementId }, "lme/dispatch/Movement").then(result => {
                const moveRow = this.layoutCarrierRates.mainDataSource.activeRow;
                let pnnRate: Currency;
                const overrideRate = moveRow?.get("override_targetpay", orderRow.get("pnn_rate", null)) as Currency;
                const targetPay = moveRow?.get("target_pay", orderRow.get("pnn_rate", null)) as Currency;

                const loadboardDef = LMEGeneralSettings.getSingleton().getDispatchControl().web_def_loadboard_post_rate;
                const useTarget = loadboardDef === null ? true : loadboardDef === "T";

                if (isPostedMovementRowSelected) {
                    pnnRate = !StringUtil.isEmptyString(overrideRate) ? overrideRate : useTarget ? targetPay : new Currency(0);
                } else if (useTarget) {
                    pnnRate = !StringUtil.isEmptyString(targetPay) ? targetPay : orderRow.get("pnn_rate");
                } else {
                    pnnRate = orderRow.get("pnn_rate") ? orderRow.get("pnn_rate") : new Currency(0);
                }


                if (pnnRate?.amount >= 0) {
                    orderRow.set("pnn_rate", pnnRate.amount);
                    orderRow.set("pnn_rate_type", "F");
                }
            });
        }
    }

    private setDisplayEquipTypeOptions() {
        const shouldDisplay = this.mainDataSource.activeRow.get("show_equip_type_options");
        this.checkboxTarps.visible = shouldDisplay;
        this.checkboxExpedited.visible = shouldDisplay;
    }

    textboxPnnLoadboardUserIdBeforeLookupModelSearch(event) {
        event.filter.web_user_type = "U";
    }

    textboxPnnLoadboardUserIdOnChange(event) {
        const orderRow = this.mainDataSource.activeRow;
        if (orderRow != null) {
            this.setUpdatedUserPnnCallbackNumber(orderRow.get("pnn_loadboard_user_id"));
        }
    }

    private async setUpdatedUserPnnCallbackNumber(userId: string) {
        if (userId != null) {
            await new ModelUsers().searchSingle({ id: userId }, "common/UserLookup").then(userRow => {
                if (!userRow) return;

                const lmData = new ModelRow("common/users", false, {
                    id: userRow.get("id"),
                    phone: userRow.get("phone")
                });

                if (userRow.get("phone")) {
                    this.mainDataSource.activeRow.set("pnn_callback", userRow.get("phone"));
                    this.textboxPnnCallback.text = userRow.get("phone");
                } else {
                    this.mainDataSource.activeRow.set("pnn_callback", "");
                    this.textboxPnnCallback.text = "";
                }
            })
        }

    }

    saveDataSources() {
        throw new Error("Function not implemented.");
    }

    static showInSlideoutDecorator(orderId: any, postTitle: string, originStopId: any, movementId: any, exceptionsList: any, isChangedMovement: boolean, pbwLoadboardServices: any) {
        const layout = Layout.getLayout("lme/dispatch/PostToLoadBoards") as PostToLoadBoards;
        const decorator = new EditRowDecorator({
            titleProps: { text: postTitle, marginLeft: 15 },
            layout: layout,
            fillVerticalSpace: true,
            width: 1000,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            saveButtonProps: {
                caption: "Save & Close",
                enabled: true,
                visible: true
            },
            onClose: () => {
                layout.slideOut();
            },
            doAfterSlideIn: async () => {
                await layout.mainDataSource.search({ id: orderId }).then(response => {
                    layout.mainDataSource.mode = DataSourceMode.UPDATE;
                    layout.mainDataSource.activeRow.set("loadboard", "Y");
                    layout.mainDataSource.addBeforeExecutionListener(() => {
                        const equipmentTypeOptions: string[] = [];
                        if (layout.checkboxTarps.checked) {
                            equipmentTypeOptions.push(equipmentTypeOptionsMap.tarps);
                        }
                        if (layout.checkboxExpedited.checked) {
                            equipmentTypeOptions.push(equipmentTypeOptionsMap.expedited);
                        }
                        layout.mainDataSource.activeRow.set("equipment_type_options", equipmentTypeOptions.join("|"));
                    })
                    layout.setDefaultById(originStopId, movementId, exceptionsList, isChangedMovement);
                });
            },
            onValidate: () => {
                const validationResults: ValidationResult[] = layout.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(
                        validationResults.filter(result => !result.isValid),
                        null,
                        layout.mainDataSource
                    );
                }
                return validationResults;
            },
            onSave: async () => {
                layout.mainDataSource.activeRow.set("loadboard_post_movement_id", movementId);
                layout.mainDataSource.activeRow.set("loadboard_posting_cancelled", false);
                return await layout.postDataSources();
            }
        });
        return decorator;
    }

    static showMultiSelectSlideout(selectedRows: TableRow[]) {
        const layout = Layout.getLayout("lme/dispatch/PostToLoadBoards") as PostToLoadBoards;
        layout.addLayoutLoadListener(async _ => {
            layout.switchDefaultUser.visible = true;
            layout.textboxDefaultUserPostingRole.visible = true;
            layout.panelUserInfo.visible = false;
            layout.switchTargetRate.visible = true;
            layout.panelRates.visible = false;
            layout.layoutCarrierRates.visible = false;
            layout.panelScheduling.visible = false;

            const loadboardDef = LMEGeneralSettings.getSingleton().getDispatchControl().web_def_loadboard_post_rate;
            layout.switchTargetRate.checked = loadboardDef === null ? true : loadboardDef === "T";
            layout.panelRates.visible = loadboardDef === "N";

            const lmData = await layout.getCurrentUserLookupModelData();
            if (lmData) {
                layout.textboxPnnLoadboardUserId.text = lmData.get("name");
                layout.textboxPnnLoadboardUserId['_lookupModelData'] = [lmData];
                layout.textboxPnnCallback.text = lmData.get("phone");
            }
            layout.textboxPnnLoadboardUserId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
                if (event.hasSelection()) {
                    layout.textboxPnnCallback.text = event.selection.get("phone");
                } else if (event.hasDeselection()) {
                    layout.textboxPnnCallback.text = "";
                }
            });

            const defaultPostingUserRole = getDispatchControlValue("default_posting_user_role") ?? "O";
            layout.textboxDefaultUserPostingRole.selectedItem = new DropdownItem(defaultPostingUserRole, "Operations User");
        });

        const decorator = new EditRowDecorator({
            titleProps: { text: "Multi-Select Post to Load Boards", marginLeft: 15 },
            layout: layout,
            fillVerticalSpace: true,
            width: 1000,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            onClose: () => {
                layout.slideOut();
            },
            onSave: async () => {
                layout.busy = true;
                const rowsToPost = [];
                const autoFailRows = [];
                const splitStopTypes = ['SP', 'SD'];
                selectedRows.forEach(row => {
                    const data = row.data;
                    //filter out legs of a Y-Split where another leg has been posted
                    const isPostedByMovement = data?.get("posted_by_movement");
                    const isPostedMovementRowSelected = data?.get("is_posted_movement");
                    if (isPostedByMovement && !isPostedMovementRowSelected) {
                        data.set("error", "Another movement of this order has already been posted");
                        autoFailRows.push(row);
                        return;
                    }
                    //filter out legs of a Y-Split where we are already posting another leg
                    const origStopType = data?.get("origin_stop_type");
                    const destStopType = data?.get("destination_stop_type");
                    const order_id = data?.get("order_id");
                    if (rowsToPost.some(r =>
                        (splitStopTypes.includes(origStopType) || splitStopTypes.includes(destStopType))
                        && r.data?.get("order_id") === order_id)) {
                        data.set("error", "Another movement of this order has already been posted");
                        autoFailRows.push(row);
                        return;
                    }
                    rowsToPost.push(row);
                });

                const options: string[] = [];
                if (layout.checkboxTarps.checked) {
                    options.push(equipmentTypeOptionsMap.tarps);
                }
                if (layout.checkboxExpedited.checked) {
                    options.push(equipmentTypeOptionsMap.expedited);
                }
                const equipmentTypeOptions = options.join("|");

                const requestBody = {
                    key_data: rowsToPost.map(row => ({
                        order_id: row.data?.get("order_id"),
                        movement_id: row.data?.get("movement_id")
                    })),
                    use_default_user: layout.switchDefaultUser.checked,
                    pnn_loadboard_user_id: layout.textboxPnnLoadboardUserId.lookupModelData?.[0].getString("id"),
                    pnn_callback: layout.textboxPnnCallback.text,
                    all_vendors_enabled: layout.switchPostToVendors.checked,
                    preferred_vendors: layout.textboxVendors.lookupModelData?.map(row => row.getString("id")),
                    post_target: layout.switchTargetRate.checked,
                    pnn_rate: layout.textboxPnnRate.text,
                    pnn_rate_type: layout.textboxPnnRateType.selectedItem?.value,
                    pnn_comment: layout.textboxPnnComment.text,
                    pnn_comment2: layout.textboxPnnComment2.text,
                    pnn_trailer_length: layout.textboxPnnTrailerLength.text,
                    pnn_trailer_width: layout.textboxPnnTrailerWidth.text,
                    equipment_type_options: equipmentTypeOptions
                };

                await decorator.slideOut();
                const downloadSnackbar = Snackbar.showDownloadSnackbar(
                    "Multi-Select Action in Progress", `Posting ${selectedRows.length} movement(s) to Load Boards...`, {
                    persist: true,
                    targetPanel: ScreenStack.getSnackbarTarget()
                });
                try {
                const response = await Api.post("lme/dispatch/loadboard-multi-post", requestBody);
                const statusList = response?.data;
                const updated = [];
                const failed = autoFailRows;
                const errors = [];
                statusList.forEach(status => {
                    const row = rowsToPost.find(r => r.data?.get("movement_id") === status.movement_id);
                    if (status.success) {
                        updated.push(row);
                    } else {
                        row?.data.set("error", status.error);
                        failed.push(row);
                    }
                    if (!row.data?.isNull("loadboard_exception_list") && row.data?.get("loadboard_exception_list").length > 0) {
                        const errorRow = row.data?.createBasicCopy();
                        errorRow.set("error", row.data?.get("loadboard_exception_list").join("\n"));
                        errors.push(errorRow);
                    }
                });
                OrderMultiSelectErrorModal.showModal(failed, errors, updated.length, "Post to Load Board Status", "Retry",
                    (selectedErrorRows) => this.showMultiSelectSlideout(selectedErrorRows));
                } finally {
                    downloadSnackbar.dismiss();
                    selectedRows[0].table.selectedRows = [];
                }
            }
        });
    }

    private async getCurrentUserLookupModelData() {
        const userId = LMEUserSettings.getUserId();
        const userRow = await new ModelUsers().searchSingle({ id: userId }, "common/UserLookup")
        if (userRow) {
            const lmData = new ModelRow("common/users", false, {
                id: userRow.get("id"),
                name: userRow.get("name"),
                phone: userRow.get("phone")
            });
            lmData.type = ModelRowType.LOOKUP_MODEL_DATA;
            return lmData;
        }
        return null;
    }

    private async getPbwLoadboardServiceList() {
        const response = await Api.post("lme/general/powerbroker/get-load-board-services");
        return response?.data[0]?.load_board_services ?? false;
    }
}
